import type { $Fetch } from 'ofetch';
import type {
  Agenciador, UpsertAgenciadorCommand,
  EmailTemplate, UpsertEmailTemplateCommand,
  ExternalIntegration, UpsertExternalIntegrationCommand,
  TenantFinancialConfig, UpsertTenantFinancialConfigCommand,
} from '@/domain/model/Fundacao';
import type {
  AgenciadorGateway, EmailTemplateGateway,
  ExternalIntegrationGateway, TenantFinancialConfigGateway,
} from '@/application/port/out/FundacaoGateways';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/** Agenciadores. Authorities `org:agenciador:read|manage`. */
export class HttpAgenciadorGateway implements AgenciadorGateway {
  private readonly path = '/api/v1/agenciadores';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<Agenciador[]> {
    const res = await this.client<ApiResponse<Agenciador[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertAgenciadorCommand): Promise<Agenciador> {
    const res = await this.client<ApiResponse<Agenciador>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertAgenciadorCommand): Promise<Agenciador> {
    const res = await this.client<ApiResponse<Agenciador>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Templates de e-mail. Authorities `identity:emailtemplate:read|manage`. */
export class HttpEmailTemplateGateway implements EmailTemplateGateway {
  private readonly path = '/api/v1/email-templates';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<EmailTemplate[]> {
    const res = await this.client<ApiResponse<EmailTemplate[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertEmailTemplateCommand): Promise<EmailTemplate> {
    const res = await this.client<ApiResponse<EmailTemplate>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertEmailTemplateCommand): Promise<EmailTemplate> {
    const res = await this.client<ApiResponse<EmailTemplate>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
  async preview(id: string, vars: Record<string, string>): Promise<string> {
    const res = await this.client<ApiResponse<string>>(
      `${this.path}/${encodeURIComponent(id)}/preview`, { method: 'POST', body: vars });
    assertApiSuccess(res); return res.data;
  }
}

/** Integrações externas. Authorities `org:integracao:read|manage`. */
export class HttpExternalIntegrationGateway implements ExternalIntegrationGateway {
  private readonly path = '/api/v1/integracoes';
  constructor(private readonly client: $Fetch) {}
  async list(): Promise<ExternalIntegration[]> {
    const res = await this.client<ApiResponse<ExternalIntegration[]>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async create(cmd: UpsertExternalIntegrationCommand): Promise<ExternalIntegration> {
    const res = await this.client<ApiResponse<ExternalIntegration>>(this.path, { method: 'POST', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async update(id: string, cmd: UpsertExternalIntegrationCommand): Promise<ExternalIntegration> {
    const res = await this.client<ApiResponse<ExternalIntegration>>(`${this.path}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
  async remove(id: string): Promise<void> {
    await this.client<void>(`${this.path}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }
}

/** Config financeira do tenant (singleton). Authorities `org:config:read|manage`. */
export class HttpTenantFinancialConfigGateway implements TenantFinancialConfigGateway {
  private readonly path = '/api/v1/configuracoes/financeiro';
  constructor(private readonly client: $Fetch) {}
  async get(): Promise<TenantFinancialConfig> {
    const res = await this.client<ApiResponse<TenantFinancialConfig>>(this.path, { method: 'GET' });
    assertApiSuccess(res); return res.data;
  }
  async save(cmd: UpsertTenantFinancialConfigCommand): Promise<TenantFinancialConfig> {
    const res = await this.client<ApiResponse<TenantFinancialConfig>>(this.path, { method: 'PUT', body: cmd });
    assertApiSuccess(res); return res.data;
  }
}
